import type {
  AddGamePlayerType,
  AddPlayer as AddPlayerType
} from '@library/types/boardBuilder';
import { useEffect, useMemo } from 'react';
import { AddPlayer } from '@library/components/player';
import { Button } from '@library/components/buttons';
import type { LeagueGameData } from '@library/types/games';
import type { LeagueTeamPlayer } from '@library/types/player';
import { Loading } from '@library/components/Loading';
import { NoData } from '@library/components/NoData';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { filterBoardBuilderGamePlayers } from '@bo-utils/boardBuilderGame';
import { useGetTeamPlayersQuery } from '@bo-services/playersApi';

export interface BoardBuilderGamePlayersProps {
  game: LeagueGameData;
  gameDate: string;
  leagueId: string;
  onSearchPlayers: ({ value }: { value: boolean }) => void;
  onSelectPlayer: ({ game, player }: AddGamePlayerType) => void;
  onShowAllPlayersClick: () => void;
  onUnSelectPlayer: ({ game, player }: AddGamePlayerType) => void;
  searchValue: string;
  selectedPlayersList: Array<AddPlayerType>;
  selectedPositions: Array<string>;
  selectedTeams: Array<string>;
  showAllPlayers: boolean;
  teams: string;
}

export const BoardBuilderGamePlayers = ({
  leagueId,
  gameDate,
  game,
  onSearchPlayers,
  onSelectPlayer,
  onShowAllPlayersClick,
  onUnSelectPlayer,
  searchValue,
  selectedPlayersList,
  selectedPositions,
  selectedTeams,
  showAllPlayers,
  teams
}: BoardBuilderGamePlayersProps) => {
  const {
    data: gamePlayers,
    isLoading: isPlayersLoading,
    isFetching: isPlayersFetching,
    isError: isPlayersFetchError
  } = useGetTeamPlayersQuery({
    leagueId,
    gameDate,
    teams
  });

  const handleAddPlayer = (player: LeagueTeamPlayer) =>
    onSelectPlayer({ game, player });

  const handleRemovePlayer = (player: LeagueTeamPlayer) =>
    onUnSelectPlayer({ game, player });

  const isLoading = isPlayersLoading || isPlayersFetching;

  const filteredPlayers = useMemo(
    () =>
      isLoading || isPlayersFetchError
        ? []
        : filterBoardBuilderGamePlayers({
            gamePlayers,
            searchValue,
            selectedTeams,
            selectedPositions
          }),
    [
      gamePlayers,
      isLoading,
      isPlayersFetchError,
      searchValue,
      selectedPositions,
      selectedTeams
    ]
  );

  useEffect(() => {
    if (!isLoading && !isPlayersFetchError && searchValue.length) {
      onSearchPlayers({
        value: !!filteredPlayers && filteredPlayers.length > 0
      });
    }
  }, [
    filteredPlayers,
    isLoading,
    isPlayersFetchError,
    onSearchPlayers,
    searchValue
  ]);

  if (isLoading) {
    return (
      <div className="self-center">
        <Loading loadingMessage="Loading Players" className="pt-2" />
      </div>
    );
  }

  if (isPlayersFetchError) {
    return (
      <div className="self-center">
        <NoData />
      </div>
    );
  }

  if (!filteredPlayers?.length) {
    return null;
  }

  const displayPlayers = showAllPlayers
    ? filteredPlayers
    : filteredPlayers?.slice(0, 5);

  const playerCount =
    filteredPlayers && filteredPlayers?.length > 5
      ? filteredPlayers.length - 5
      : 0;

  const playersList = displayPlayers.map((player) => {
    const { name, foreign_id: playerId } = player;

    const playerAdded = selectedPlayersList.some(
      ({ game: addPlayerGame, player: addPlayer }) =>
        game.game_id === addPlayerGame.game_id &&
        player.foreign_id === addPlayer.foreign_id
    );

    return (
      <AddPlayer
        key={`${game.game_id}-${playerId ?? name}`}
        onAddPlayer={handleAddPlayer}
        onRemovePlayer={handleRemovePlayer}
        player={player}
        playerAdded={playerAdded}
      />
    );
  });

  return (
    <div className="no-scrollbar inline-flex w-[375px] flex-col items-start justify-start gap-4 overflow-scroll px-4 pb-4">
      {playersList}
      {!showAllPlayers && playerCount > 0 && (
        <Button
          className="font-heebo flex items-center text-sm font-medium leading-[19px] tracking-tight text-neutral-300"
          onClick={onShowAllPlayersClick}
        >
          {`${playerCount} more players`}
          <img alt="Chevron Down icon" src={chevronDownIcon} />
        </Button>
      )}
    </div>
  );
};
